import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type FC,
} from "react";
import { useDispatch } from "react-redux";
import {
  type AirCfarOffer,
  type AirCfarOfferV2,
  FareRefundabilityV2 as FareRefundability,
  type FiatPrice,
  type TrackingProperties,
} from "@b2bportal/air-cfar-api";
import {
  FintechProduct,
  FintechProductBullet,
  type FintechProductOptionsSectionItemProps,
} from "@b2bportal/core-fintech-ui";
import {
  type GetElementClassNameFn,
  IconNameEnum,
  useCfarStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  type AppDispatch,
  CoreFlightsCfarOffersComponents,
  FlightsCfarTrackingEvent,
  type SelectedCfarOfferStatus,
  SelectedStatus,
} from "@b2bportal/core-types";
import { useDeviceType } from "@b2bportal/core-utilities";
import { CfarHowItWorksDialog } from "@components/core-flights-cfar-offers";
import { trackEvent } from "@hopper-b2b/api";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { InfoBanner, InfoBannerSeverity } from "@tenant/ui";
import { clsx } from "clsx";
import { FlightsCfarOffersActions } from "../../store";
import { getCurrencyFormatOptions } from "../../utils";

import defaultStyles from "./CfarOfferSection.module.scss";

export interface CfarOfferSectionProps {
  className?: string;
  cfarOffers: (AirCfarOffer | AirCfarOfferV2)[];
  termsAndConditionsLink: string;
  selectedCfarOffer: SelectedCfarOfferStatus;
  cfarOfferTrackingProperties: TrackingProperties[] | undefined;
  showCloseButtonInDialog?: boolean;
  onSelect?: (cfarOffer: SelectedCfarOfferStatus) => void;
  cfarFareRefundability?: FareRefundability;
}

// Selectable cfar offer component
export function CfarOfferSection({
  className,
  cfarOffers,
  termsAndConditionsLink,
  selectedCfarOffer,
  cfarOfferTrackingProperties,
  cfarFareRefundability,
  showCloseButtonInDialog,
  onSelect,
}: CfarOfferSectionProps) {
  const COMPONENT_KEY = CoreFlightsCfarOffersComponents.CfarOfferSection;
  const styles = useCfarStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const dispatch = useDispatch<AppDispatch>();

  const { t, formatFiatCurrency } = useI18nContext();
  const { isMobile } = useDeviceType();

  const handleCfarOfferSelect = (
    cfarOffer: AirCfarOffer | AirCfarOfferV2 | null
  ) => {
    const selection: SelectedCfarOfferStatus =
      cfarOffer == null
        ? { status: SelectedStatus.DECLINED }
        : {
            status: SelectedStatus.SELECTED,
            offer: cfarOffer,
          };

    onSelect?.(selection);
    dispatch(FlightsCfarOffersActions.setSelectedCfarOffer(selection));
  };

  const currencyFormatOptions = useMemo(() => getCurrencyFormatOptions(), []);

  const formatPricesNoFraction = (price: FiatPrice): string =>
    formatFiatCurrency(price, currencyFormatOptions);

  const calledTrackViewCfarEvent = useRef(false);
  useEffect(() => {
    callOnce(calledTrackViewCfarEvent, () => {
      if (cfarOfferTrackingProperties != null) {
        const aggregatedProperties = cfarOfferTrackingProperties.reduce(
          (acc, cur) => {
            return {
              ...cur.properties,
              ...acc,
            };
          },
          {}
        );
        if (aggregatedProperties != null) {
          trackEvent({
            eventName: FlightsCfarTrackingEvent.VIEW_CFAR,
            properties: {
              ...aggregatedProperties,
            },
            encryptedProperties: cfarOfferTrackingProperties.flatMap(
              (offer) => offer.encryptedProperties ?? []
            ),
          });
        }
      }
    });
  }, [cfarOfferTrackingProperties]);

  const cfarProperties = useCfarOfferSection({
    cfarOffers,
    priceFormatter: formatPricesNoFraction,
    selectedCfarOffer,
    t,
    cn,
    handleCfarOfferSelect,
    isMobile,
    cfarFareRefundability,
  });
  const { allCfarOptions, body, coverageAmount } = cfarProperties;

  return (
    <div className={clsx(block, className)}>
      <FintechProduct
        title={t("core-flights-cfar.productName")}
        iconName={IconNameEnum.cancelForAnyReason}
        selectionOffers={{
          options: allCfarOptions,
        }}
        viewTermsLink={termsAndConditionsLink}
        howItWorksDialog={
          <CfarHowItWorksDialog
            coverageAmount={coverageAmount}
            termsAndConditionsLink={termsAndConditionsLink}
            cfarFareRefundability={cfarFareRefundability}
          />
        }
        showCloseButtonInDialog={showCloseButtonInDialog}
        infoLinksUI={{
          prefixIcon: {
            iconName: IconNameEnum.infoFilled,
            className: cn("info-link-icon"),
          },
          // Keep terms in-app (iframe modal), same as DAFAR / DisruptionOfferSectionWrapper.
          // target="_blank" drops Nubank Android/iOS WebView checkout state (NVH-1013).
          displayViewTermsAsModal: true,
        }}
      >
        {body}
      </FintechProduct>
    </div>
  );
}

function useCfarOfferSection({
  cfarOffers,
  priceFormatter,
  selectedCfarOffer,
  t,
  cn,
  handleCfarOfferSelect,
  isMobile,
  cfarFareRefundability,
}: {
  cfarOffers: (AirCfarOffer | AirCfarOfferV2)[];
  priceFormatter: (price: FiatPrice) => string;
  selectedCfarOffer: SelectedCfarOfferStatus;
  t: any;
  cn: GetElementClassNameFn;
  handleCfarOfferSelect: (
    cfarOffer: AirCfarOffer | AirCfarOfferV2 | null
  ) => void;
  isMobile: boolean;
  cfarFareRefundability?: FareRefundability;
}): {
  coverageAmount: string;
  allCfarOptions: FintechProductOptionsSectionItemProps[];
  body: JSX.Element;
} {
  const coverageAmount = priceFormatter(cfarOffers[0].coverageAmount.fiat);
  const cfarOfferOptions =
    cfarOffers.map<FintechProductOptionsSectionItemProps>(
      (cfarOffer: AirCfarOffer | AirCfarOfferV2) => {
        const premiumAmount = priceFormatter(cfarOffer.premiumAmount.fiat);
        const coveragePercentage = getCfarCoveragePercentage(cfarOffer);

        return {
          title: (
            <Trans
              i18nKey="core-flights-cfar.optionTitle"
              values={{
                coveragePercentage: `${coveragePercentage}%`,
              }}
              components={{
                strong: <span className={cn("bold")} />,
                primary: <div className={cn("option-title", ["primary"])} />,
                secondary: (
                  <div className={cn("option-title", ["secondary"])} />
                ),
              }}
            />
          ),
          description: (
            <Trans
              i18nKey="core-flights-cfar.costPerTraveler"
              values={{
                premiumAmount,
              }}
              components={[
                <span className={clsx(cn("bold"), cn("traveller-cost"))} />,
              ]}
            />
          ),
          selected:
            selectedCfarOffer.status === SelectedStatus.SELECTED &&
            selectedCfarOffer.offer.quoteId === cfarOffer.quoteId,
          onClick: () => handleCfarOfferSelect(cfarOffer),
          selectedContent: {
            icon: {
              iconName: IconNameEnum.checkFilled,
            },
            children: (
              <Trans
                i18nKey="core-flights-cfar.offerSelected"
                values={{
                  coveragePercentage: `${coveragePercentage}%`,
                  premiumAmount,
                }}
                components={[<span className={cn("bold")} />]}
              />
            ),
          },
        };
      }
    );

  const allCfarOptions = [
    ...cfarOfferOptions,
    {
      title: t("core-flights-cfar.declineOptionTitle"),
      description: (
        <span>
          {isMobile
            ? t("core-flights-cfar.declineOptionSubtitleMobile")
            : t("core-flights-cfar.declineOptionSubtitle")}
        </span>
      ),
      selected: selectedCfarOffer.status === SelectedStatus.DECLINED,
      onClick: () => handleCfarOfferSelect(null),
      selectedContent: {
        icon: {
          iconName: IconNameEnum.warning,
          className: cn("declined-icon"),
        },
        children: t("core-flights-cfar.offerDeclined"),
      },
    },
  ];

  return {
    allCfarOptions,
    body: (
      <CfarOfferSectionBody
        cfarOffers={cfarOffers}
        priceFormatter={priceFormatter}
        cfarFareRefundability={cfarFareRefundability}
      />
    ),
    coverageAmount,
  };
}

function CfarOfferSectionBody(
  props: Pick<CfarOfferSectionProps, "cfarOffers" | "cfarFareRefundability"> & {
    priceFormatter: (price: FiatPrice) => string;
  }
) {
  const { cfarOffers, priceFormatter, cfarFareRefundability } = props;

  const [block, cn] = useModuleBEM(
    useCfarStyles(
      CoreFlightsCfarOffersComponents.CfarOfferSectionBody,
      defaultStyles
    ),
    CoreFlightsCfarOffersComponents.CfarOfferSectionBody
  );

  const coverageAmount = priceFormatter(cfarOffers[0].coverageAmount.fiat);
  const coveragePercentage = getCfarCoveragePercentage(cfarOffers[0]);

  return (
    <div className={block}>
      <div className={cn("bullets")}>
        <FintechProductBullet
          icon={{
            iconName: IconNameEnum.checkBulletPoint,
            className: cn("check-icon"),
          }}
          className={cn("body-item")}
        >
          <Trans
            i18nKey="core-flights-cfar.bodyOne"
            components={[<span className={cn("bold")} />]}
          />
        </FintechProductBullet>
        <FintechProductBullet
          icon={{
            iconName: IconNameEnum.checkBulletPoint,
            className: cn("check-icon"),
          }}
          className={cn("body-item")}
        >
          <Trans
            i18nKey="core-flights-cfar.bodyTwo"
            values={{
              refundAmount: coverageAmount,
              coveragePercentage: `${coveragePercentage}%`,
            }}
            components={[<span className={cn("bold")} />]}
          />
        </FintechProductBullet>
        <FintechProductBullet
          icon={{
            iconName: IconNameEnum.checkBulletPoint,
            className: cn("check-icon"),
          }}
          className={cn("body-item")}
        >
          <Trans i18nKey="core-flights-cfar.bodyThree" />
        </FintechProductBullet>
      </div>
      <InformationalDisclaimerSection
        className={cn("disclaimer")}
        cfarFareRefundability={cfarFareRefundability}
        cn={cn}
      />
    </div>
  );
}

const InformationalDisclaimerSection: FC<{
  className?: string;
  cfarFareRefundability: FareRefundability | undefined;
  cn: GetElementClassNameFn;
}> = ({ className, cfarFareRefundability, cn }) => {
  const NonRefundableInfoBanner = useCallback(
    () => (
      <InfoBanner
        severity={InfoBannerSeverity.INFO}
        icon={IconNameEnum.informational}
      >
        <Trans
          className={cn("non-refundable-text")}
          i18nKey="core-flights-cfar.informationalBanner"
          components={{
            strong: <strong className={cn("bold")} />,
          }}
        />
      </InfoBanner>
    ),
    [cn]
  );

  const FTCDisclaimerSection = useCallback(
    () => (
      <div className={cn("ftc-disclaimer")}>
        <Trans i18nKey="core-flights-cfar.ftcDisclaimer" />
      </div>
    ),
    [cn]
  );

  const isFTC =
    cfarFareRefundability === FareRefundability.FullFtc ||
    cfarFareRefundability === FareRefundability.PartialFtc;
  return (
    <div className={className}>
      {isFTC ? <FTCDisclaimerSection /> : <NonRefundableInfoBanner />}
    </div>
  );
};

function callOnce(called: MutableRefObject<boolean>, fn: () => void) {
  if (called.current) {
    return;
  }
  fn();
  called.current = true;
}

function getCfarCoveragePercentage(
  cfarOffer: AirCfarOffer | AirCfarOfferV2
): string {
  return typeof cfarOffer.coveragePercentage === "string"
    ? cfarOffer.coveragePercentage
    : cfarOffer.coveragePercentage.value.toString();
}
